
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { AuthProvider } from "@/hooks/useAuth";
import { ScrollToTop } from "@/components/ScrollToTop";
import { LanguageRedirect } from "@/components/LanguageRedirect";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Blog from "./pages/Blog";
import BlogAutoUpdate from "./pages/BlogAutoUpdate";
import BlogSalesReps from "./pages/BlogSalesReps";
import BlogRepsCRMs from "./pages/BlogRepsCRMs";
import BlogAICopilots from "./pages/BlogAICopilots";
import BlogDataQuality from "./pages/BlogDataQuality";
import BlogHubSpotSalesforce from "./pages/BlogHubSpotSalesforce";
import BlogVoiceROI from "./pages/BlogVoiceROI";
import BlogSMBsCopilot from "./pages/BlogSMBsCopilot";
import BlogAutoUpdate2025 from "./pages/BlogAutoUpdate2025";
import BlogGDPRCopilot from "./pages/BlogGDPRCopilot";
import PrivacyEU from "./pages/PrivacyEU";
import Comparison from "./pages/Comparison";
import ComparisonSalesforce from "./pages/ComparisonSalesforce";
import ComparisonHubSpot from "./pages/ComparisonHubSpot";
import AIAgentSalesforce from "./pages/AIAgentSalesforce";
import TryApp from "./pages/TryApp";
import ForAI from "./pages/ForAI";
import Documentation from "./pages/Documentation";
import TechnicalFAQ from "./pages/TechnicalFAQ";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import About from "./pages/About";
import Pricing from "./pages/Pricing";
import Trial from "./pages/Trial";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <LanguageRedirect />
          <AnimatePresence mode="wait">
            <Routes>
              {/* English Routes */}
              <Route path="/" element={<Index />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/how-to-update-salesforce-automatically" element={<BlogAutoUpdate />} />
              <Route path="/blog/why-sales-reps-hate-crms" element={<BlogRepsCRMs />} />
              <Route path="/blog/ai-copilots-sales-teams-2025" element={<BlogAICopilots />} />
              <Route path="/blog/salesforce-data-quality-ai-voice-assistants" element={<BlogDataQuality />} />
              <Route path="/blog/hubspot-vs-salesforce-ai-integration-2025" element={<BlogHubSpotSalesforce />} />
              <Route path="/blog/voice-assistant-technology-sales-teams-roi-calculator" element={<BlogVoiceROI />} />
              <Route path="/blog/best-salesforce-ai-copilot-smbs-2025" element={<BlogSMBsCopilot />} />
              <Route path="/blog/update-salesforce-automatically-2025" element={<BlogAutoUpdate2025 />} />
              <Route path="/blog/gdpr-compliant-salesforce-ai-copilot-europe" element={<BlogGDPRCopilot />} />
              <Route path="/privacy-eu" element={<PrivacyEU />} />
              <Route path="/comparison" element={<Comparison />} />
              <Route path="/comparison/salesforce-einstein" element={<ComparisonSalesforce />} />
              <Route path="/comparison/hubspot" element={<ComparisonHubSpot />} />
              <Route path="/ai-agent-salesforce" element={<AIAgentSalesforce />} />
              <Route path="/app" element={<TryApp />} />
              <Route path="/for-ai" element={<ForAI />} />
              <Route path="/docs" element={<Documentation />} />
              <Route path="/faq" element={<TechnicalFAQ />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/about" element={<About />} />
              <Route path="/trial" element={<Trial />} />
              
              {/* French Routes */}
              <Route path="/fr" element={<Index />} />
              <Route path="/fr/blog" element={<Blog />} />
              <Route path="/fr/blog/comment-mettre-a-jour-salesforce-automatiquement" element={<BlogAutoUpdate />} />
              <Route path="/fr/blog/pourquoi-les-commerciaux-detestent-les-crm" element={<BlogRepsCRMs />} />
              <Route path="/fr/blog/copilotes-ia-equipes-commerciales-2025" element={<BlogAICopilots />} />
              <Route path="/fr/blog/qualite-donnees-salesforce-assistants-vocaux-ia" element={<BlogDataQuality />} />
              <Route path="/fr/blog/hubspot-vs-salesforce-integration-ia-2025" element={<BlogHubSpotSalesforce />} />
              <Route path="/fr/blog/technologie-assistant-vocal-equipes-commerciales-calculateur-roi" element={<BlogVoiceROI />} />
              <Route path="/fr/blog/meilleur-copilote-ia-salesforce-pme-2025" element={<BlogSMBsCopilot />} />
              <Route path="/fr/blog/mettre-a-jour-salesforce-automatiquement-2025" element={<BlogAutoUpdate2025 />} />
              <Route path="/fr/blog/copilote-ia-salesforce-conforme-rgpd-europe" element={<BlogGDPRCopilot />} />
              <Route path="/fr/confidentialite-ue" element={<PrivacyEU />} />
              <Route path="/fr/comparison" element={<Comparison />} />
              <Route path="/fr/comparison/salesforce-einstein" element={<ComparisonSalesforce />} />
              <Route path="/fr/comparison/hubspot" element={<ComparisonHubSpot />} />
              <Route path="/fr/agent-ia-salesforce" element={<AIAgentSalesforce />} />
              <Route path="/fr/app" element={<TryApp />} />
              <Route path="/fr/documentation" element={<Documentation />} />
              <Route path="/fr/faq" element={<TechnicalFAQ />} />
              <Route path="/fr/politique-confidentialite" element={<PrivacyPolicy />} />
              <Route path="/fr/conditions-utilisation" element={<TermsOfService />} />
              <Route path="/fr/tarifs" element={<Pricing />} />
              <Route path="/fr/a-propos" element={<About />} />
              <Route path="/fr/essai" element={<Trial />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </AnimatePresence>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
